import { CSSObject } from 'styled-components';
import Select from 'react-select';
import { FC, ReactElement, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import CreatableSelect from 'react-select/creatable';

interface IStyleObject {
  [key: string]: any;
}

const createStyles = (
  needHighlightFirstOption?: boolean,
  singleValueColor?: string
): IStyleObject => {
  return {
    control: (provided: any, state: any) => {
      return {
        ...provided,
        backgroundColor: '#211F24',
        minHeight: state.selectProps.isFilter ? '42px' : '30px',
        borderColor: 'none',
        border: 'none',
        color: 'white',
        boxShadow: 'none',
        width: '100%',
        cursor: 'pointer',
      };
    },
    dropdownIndicator: (provided: any, state: any) => ({
      ...provided,
      padding: 5,
    }),
    clearIndicator(base: CSSObject): CSSObject {
      base.padding = 0;
      base.display = 'none';
      // иконка отмены всех опшенов
      return base;
    },
    indicatorsContainer(base: CSSObject): CSSObject {
      base.padding = 10;
      base.display = 'none';
      // область открытия дропдауна

      return base;
    },
    placeholder: (base: CSSObject): CSSObject => {
      return {
        ...base,
        color: '#ffffff',
        fontSize: '12px',
      };
    },
    noOptionsMessage: (base: CSSObject): CSSObject => {
      return {
        ...base,
        backgroundColor: '#252328',
        color: 'white',
      };
    },
    menu: (base: CSSObject): CSSObject => {
      return {
        ...base,
        backgroundColor: '#252328',
        color: 'white',
        border: '1px solid #211F24',
        borderRadius: '10px',
        overflow: 'hidden',
        width: '100%',
        zIndex: 5,
      };
    },
    indicatorSeparator(base: CSSObject): CSSObject {
      base.padding = 0;
      base.display = 'none';
      // vertical line between icons in input

      return base;
    },
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: '#252328',
      color: 'white',
      cursor: 'pointer',
      width: '100%',
      '&:first-child': {
        color: needHighlightFirstOption ? '#D7F525' : 'white',
      },
    }),
    singleValue: (provided: any, state: any) => ({
      ...provided,
      color: singleValueColor,
    }),

    input: (provided: any, state: any) => ({
      ...provided,
      input: {
        margin: '0px',
        color: 'white',
      },
      height: '29px',
      fontSize: '12px',
      paddingTop: '7px',
      width: '100%',
      color: 'white',
      paddingBottom: '7px',
      marginTop: 0,
      marginBottom: 0,
    }),
  };
};

interface ICustomSelectProps {
  options: any;
  title: string;
  isLoading: boolean;
  width?: number | string;
  height?: string;
  onChange?: (values: ISelectOption | null) => void;
  value?: ISelectOption | null;
  needHighlightFirstOption?: boolean;
  singleValueColor?: string;
  textSize?: string;
  bgColor?: string;
  hideClearIcon?: boolean;
  icon?: ReactElement;
  isCreatable?: boolean;
}

export interface ISelectOption {
  value: string;
  label: string;
}

const SingleSelect: FC<ICustomSelectProps> = ({
  options,
  title,
  onChange,
  isLoading = false,
  width = 150,
  height = '33px',
  value,
  needHighlightFirstOption = false,
  singleValueColor = 'white',
  bgColor = '#211F24',
  hideClearIcon = false,
  textSize = '12px',
  icon,
  isCreatable = false,
}) => {
  const selectRef = useRef(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const styles = useMemo(() => {
    return createStyles(needHighlightFirstOption, singleValueColor);
  }, [needHighlightFirstOption]);

  return (
    <div
      style={{
        minWidth: `${Number.isInteger(width) ? width + 'px' : width}`,
        maxWidth: `${Number.isInteger(width) ? width + 'px' : width}`,
      }}
      className={clsx(
        'rounded-[8px] text-yellow-300 cursor-pointer relative',
        `bg-[${bgColor}] text-[white] text-[${textSize}] h-[${height}]`,
        isOpen ? 'z-[100]' : 'z-[10]'
      )}
    >
      <div className="absolute left-[5px] top-[50%] -translate-y-[50%]">{icon}</div>

      {isCreatable ? (
        <CreatableSelect
          ref={selectRef}
          options={options}
          menuPosition="fixed"
          isMulti={false}
          className={`h-[33px] ${icon ? 'pl-[20px]' : ''}`}
          openMenuOnFocus={false}
          hideSelectedOptions={true}
          closeMenuOnSelect={true}
          value={value ? value : ''}
          styles={styles}
          isClearable={true}
          isLoading={isLoading}
          menuIsOpen={isOpen}
          onMenuOpen={() => setIsOpen(true)}
          onMenuClose={() => setIsOpen(false)}
          formatCreateLabel={inputValue => `Создать "${inputValue}"`}
          placeholder={title}
          onChange={(options: any) => {
            if (onChange) {
              onChange(options);
            }
          }}
        />
      ) : (
        <Select
          ref={selectRef}
          options={options}
          menuPosition="fixed"
          isMulti={false}
          className={`h-[33px] ${icon ? 'pl-[20px]' : ''}`}
          openMenuOnFocus={false}
          hideSelectedOptions={true}
          closeMenuOnSelect={true}
          value={value ? value : ''}
          styles={styles}
          isClearable={true}
          isLoading={isLoading}
          menuIsOpen={isOpen}
          onMenuOpen={() => setIsOpen(true)}
          onMenuClose={() => setIsOpen(false)}
          placeholder={title}
          onChange={(options: any) => {
            if (onChange) {
              onChange(options);
            }
          }}
        />
      )}
      <div
        className="absolute right-[10px] top-[50%] -translate-y-[50%] cursor-pointer"
        onClick={() => setIsOpen(prevState => !prevState)}
      >
        <ChevronDown
          className={clsx(
            'w-4 h-4 text-white transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </div>
    </div>
  );
};

export default SingleSelect;
